<script setup>
/* global axios */
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import FileUpload from 'vue-upload-component';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  MAXIMUM_FILE_UPLOAD_SIZE,
  ALLOWED_FILE_TYPES,
} from 'shared/constants/messages';
import { emitter } from 'shared/helpers/mitt';
import Modal from 'dashboard/components/Modal.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import KanbanAPI from '../../../../api/kanban';
import MarkdownIt from 'markdown-it';

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
  kanbanItems: {
    type: Array,
    default: () => [],
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  contactsList: {
    type: Array,
    default: () => [],
  },
  loadingItems: {
    type: Boolean,
    default: false,
  },
  loadingConversations: {
    type: Boolean,
    default: false,
  },
  loadingContacts: {
    type: Boolean,
    default: false,
  },
  currentUser: {
    type: Object,
    required: true,
  },
  agentList: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits([
  'update:checklists',
  'update:item',
  'item-updated',
  'add-checklist-item',
  'edit-checklist-item',
  'delete-checklist-item',
  'toggle-checklist-item',
  'upload-attachment',
  'delete-attachment'
]);

const { t } = useI18n();

// Instância do MarkdownIt com configurações padrão
const md = new MarkdownIt({
  html: false,      // Desabilitar HTML inline para segurança
  breaks: true,     // Converter quebras de linha em <br>
  linkify: true     // Converter URLs em links automaticamente
});

// Função para renderizar markdown
const renderMarkdown = (text) => {
  if (!text) return '';

  // Renderizar diretamente - markdown-it já suporta listas por padrão
  return md.render(text);
};

// Refs para o componente
const currentChecklistItem = ref('');
const savingChecklist = ref(false);
const editingItemId = ref(null);
const editingItemText = ref('');
const checklistAttachments = ref([]);
const isUploadingAttachment = ref(false);
const selectedFileName = ref('');
const loadingChecklists = ref(false);
const internalChecklists = ref([]);

// Refs para vinculação
const showItemSelector = ref(false);
const showConversationSelector = ref(false);
const showContactSelector = ref(false);
const selectedItemId = ref(null);
const selectedConversationId = ref(null);
const selectedContactId = ref(null);

// Refs para o checklist
const hideCompletedItems = ref(false);
const checklistDueDate = ref('');
const checklistPriority = ref('none');

// Refs para dropdown de vinculação
const showLinkDropdown = ref(false);

// Refs para atribuição de agentes aos itens do checklist
const showAgentAssignModal = ref(false);
const selectedChecklistItemForAgent = ref(null);
const agentAssignLoading = ref(false);
const selectedAgentId = ref(null);
const agentSearch = ref('');

// Refs para modal de detalhes do item
const showItemDetailsModal = ref(false);
const selectedChecklistItemDetails = ref(null);

// Computed properties
const selectedLinksPreview = computed(() => {
  const links = [];
  if (selectedItemId.value) {
    links.push({ type: 'item', id: selectedItemId.value });
  }
  if (selectedConversationId.value) {
    links.push({ type: 'conversation', id: selectedConversationId.value });
  }
  if (selectedContactId.value) {
    links.push({ type: 'contact', id: selectedContactId.value });
  }
  return links;
});

// Computed para estatísticas do checklist
const checklistStats = computed(() => {
  const checklists = displayChecklists.value;

  const total = checklists.length;
  const completed = checklists.filter(item => item.completed).length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  return { total, completed, percentage };
});

// Ref para controle de ordenação
const checklistSortOrder = ref('newest'); // 'newest', 'oldest'

// Computed para usar checklists internos
const displayChecklists = computed(() => {
  // Garantir que checklists seja sempre um array
  let checklists = Array.isArray(internalChecklists.value) ? internalChecklists.value : [];

  if (!checklists || checklists.length === 0) return [];

  // Ordenar checklists baseado no critério selecionado
  return [...checklists].sort((a, b) => {
    switch (checklistSortOrder.value) {
      case 'oldest':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'newest':
      default:
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });
});

// Computed para filtrar checklists
const filteredChecklistItems = computed(() => {
  const checklists = displayChecklists.value;

  if (hideCompletedItems.value) {
    return checklists.filter(item => !item.completed);
  }
  return checklists;
});

// Computed para obter informações do agente de um item do checklist
const getChecklistItemAgent = checklistItem => {
  if (!checklistItem.agent_id) return null;

  const agent = props.agentList.find(a => a.id === checklistItem.agent_id);
  if (!agent) return null;

  return {
    id: agent.id,
    name: agent.name,
    avatar_url: agent.avatar_url || '',
  };
};

// Função para obter informações da prioridade do item do checklist
const getChecklistItemPriority = checklistItem => {
  const priority = checklistItem.priority || 'none';
  const priorityMap = {
    none: { label: 'Nenhuma', color: 'slate', iconSvg: null },
    low: { label: 'Baixa', color: 'green', iconSvg: '<path d="M7 13l3 3 3-3"/><path d="M7 6l3 3 3-3"/>' },
    medium: { label: 'Média', color: 'yellow', iconSvg: '<path d="M9 12l2 2 2-2"/><path d="M7 7l4 4 4-4"/>' },
    high: { label: 'Alta', color: 'orange', iconSvg: '<path d="M7 11l3-3 3 3"/><path d="M7 18l3-3 3 3"/>' },
    urgent: { label: 'Urgente', color: 'red', iconSvg: '<path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>' }
  };

  return priorityMap[priority] || priorityMap.none;
};

const itemButtonText = computed(() => {
  if (showItemSelector.value) {
    return t('KANBAN.CANCEL');
  }

  if (selectedItemId.value) {
    const selectedItem = props.kanbanItems.find(
      item => item.id === selectedItemId.value
    );
    return selectedItem?.title || t('KANBAN.FORM.NOTES.LINK_ITEM');
  }

  return t('KANBAN.FORM.NOTES.LINK_ITEM');
});

const conversationButtonText = computed(() => {
  if (showConversationSelector.value) {
    return t('KANBAN.CANCEL');
  }

  if (selectedConversationId.value) {
    const selectedConversation = props.conversations.find(
      conv => conv.id === selectedConversationId.value
    );
    return selectedConversation
      ? `#${selectedConversation.id} - ${selectedConversation.title}`
      : t('KANBAN.FORM.NOTES.LINK_CONVERSATION');
  }

  return t('KANBAN.FORM.NOTES.LINK_CONVERSATION');
});

const contactButtonText = computed(() => {
  if (showContactSelector.value) {
    return t('KANBAN.CANCEL');
  }

  if (selectedContactId.value) {
    const selectedContact = props.contactsList.find(
      contact => contact.id === selectedContactId.value
    );
    return selectedContact?.name || t('KANBAN.FORM.NOTES.LINK_CONTACT');
  }

  return t('KANBAN.FORM.NOTES.LINK_CONTACT');
});

// Funções utilitárias
const isImage = attachment => {
  try {
    return !!(
      attachment &&
      typeof attachment === 'object' &&
      attachment.fileType &&
      typeof attachment.fileType === 'string' &&
      attachment.fileType.startsWith('image/')
    );
  } catch (error) {
    return false;
  }
};

const hasNonImageAttachments = item => {
  return item.attachments?.some(a => !isImage(a)) || false;
};

const hasImageAttachments = item => {
  return item.attachments?.some(isImage) || false;
};

const getNonImageAttachments = item => {
  return item.attachments?.filter(a => !isImage(a)) || [];
};

const getImageAttachments = item => {
  return item.attachments?.filter(isImage) || [];
};

const getFileIcon = attachment => {
  if (!attachment || !attachment.fileType) return '📎';
  return attachment.fileType.includes('pdf') ? '📄' : '📎';
};

const formatDate = date => {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Erro ao formatar data:', error);
    return '';
  }
};

// Funções para manipulação do checklist
const handleAddChecklistItem = async () => {
  if (!currentChecklistItem.value.trim()) return;

  try {
    await KanbanAPI.createChecklistItem(props.item?.id, {
      text: currentChecklistItem.value,
      attachments: checklistAttachments.value,
      linked_item_id: selectedItemId.value,
      linked_conversation_id: selectedConversationId.value,
      linked_contact_id: selectedContactId.value,
      due_date: checklistDueDate.value || null,
      priority: checklistPriority.value,
    });

    // Buscar checklists atualizadas
    await fetchChecklists();

    // Limpar estado
    currentChecklistItem.value = '';
    checklistAttachments.value = [];
    selectedFileName.value = '';
    selectedItemId.value = null;
    selectedConversationId.value = null;
    selectedContactId.value = null;
    checklistDueDate.value = '';
    checklistPriority.value = 'none';
    editingItemId.value = null;

    // Mostrar mensagem de sucesso
    emitter.emit('newToastMessage', {
      message: 'Item do checklist criado com sucesso',
      action: { type: 'success' },
    });

  } catch (error) {
    console.error('Erro ao adicionar item do checklist:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao criar item do checklist',
      action: { type: 'error' },
    });
  }
};


const handleEditChecklistItem = item => {
  editingItemId.value = item.id;
  currentChecklistItem.value = item.text;
  selectedItemId.value = item.linked_item_id || null;
  selectedConversationId.value = item.linked_conversation_id || null;
  selectedContactId.value = item.linked_contact_id || null;
  checklistAttachments.value = item.attachments || [];
};

const handleDeleteChecklistItem = async itemId => {
  try {
    await KanbanAPI.deleteChecklistItem(props.item?.id, itemId);

    // Buscar checklists atualizadas
    await fetchChecklists();

    // Mostrar mensagem de sucesso
    emitter.emit('newToastMessage', {
      message: 'Item do checklist deletado com sucesso',
      action: { type: 'success' },
    });

  } catch (error) {
    console.error('Erro ao deletar item do checklist:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao deletar item do checklist',
      action: { type: 'error' },
    });
  }
};

const handleToggleChecklistItem = async item => {
  try {
    await KanbanAPI.toggleChecklistItem(props.item?.id, item.id);

    // Buscar checklists atualizadas
    await fetchChecklists();

  } catch (error) {
    console.error('Erro ao atualizar item do checklist:', error);
  }
};

const cancelEditItem = () => {
  editingItemId.value = null;
  currentChecklistItem.value = '';
  checklistAttachments.value = [];
  selectedFileName.value = '';
  selectedItemId.value = null;
  selectedConversationId.value = null;
  selectedContactId.value = null;
  checklistDueDate.value = '';
  checklistPriority.value = 'none';
  showItemSelector.value = false;
  showConversationSelector.value = false;
  showContactSelector.value = false;
  showLinkDropdown.value = false;
};

// Funções para upload de anexos
const handleChecklistAttachment = async file => {
  if (!file) return;

  selectedFileName.value = file.file.name;
  closeDropdownAfterUpload(); // Fecha o dropdown quando iniciar upload

  if (!props.item?.id) {
    console.error('ID do item não encontrado:', props.item);
    return;
  }

  isUploadingAttachment.value = true;
  try {
    if (checkFileSizeLimit(file, MAXIMUM_FILE_UPLOAD_SIZE)) {
      // Para checklist, vamos usar o endpoint de anexos do item
      const formData = new FormData();
      formData.append('attachment', file.file);

      const url = `/api/v1/accounts/${KanbanAPI.accountId}/kanban/items/${props.item?.id}/attachments`;

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Adicionar anexo ao item atual se estiver editando
      if (editingItemId.value) {
        const updatedAttachments = [
          ...(checklistAttachments.value || []),
          {
            id: response.data.id.toString(),
            url: response.data.attachment_url,
            filename: file.file.name,
            fileType: file.file.type || 'application/octet-stream',
            source: { type: 'checklist', id: editingItemId.value },
          },
        ];
        checklistAttachments.value = updatedAttachments;
      }

      // Recarregar checklists após upload
      await fetchChecklists();
    }
  } catch (error) {
    console.error('Erro ao fazer upload:', error.message);
  } finally {
    isUploadingAttachment.value = false;
  }
};

// Funções para dropdown de vinculação
const toggleLinkDropdown = () => {
  showLinkDropdown.value = !showLinkDropdown.value;
};

const selectLinkType = (type) => {
  showLinkDropdown.value = false;

  switch (type) {
    case 'item':
      toggleItemSelector();
      break;
    case 'conversation':
      toggleConversationSelector();
      break;
    case 'contact':
      toggleContactSelector();
      break;
  }
};

// Função para fechar dropdown após upload de arquivo
const closeDropdownAfterUpload = () => {
  showLinkDropdown.value = false;
};

// Funções para seletores
const toggleItemSelector = () => {
  if (showItemSelector.value) {
    showItemSelector.value = false;
  } else {
    showItemSelector.value = true;
    showConversationSelector.value = false;
    showContactSelector.value = false;
  }
};

const toggleConversationSelector = () => {
  if (showConversationSelector.value) {
    showConversationSelector.value = false;
  } else {
    showConversationSelector.value = true;
    showItemSelector.value = false;
    showContactSelector.value = false;
  }
};

const toggleContactSelector = () => {
  if (showContactSelector.value) {
    showContactSelector.value = false;
  } else {
    showContactSelector.value = true;
    showItemSelector.value = false;
    showConversationSelector.value = false;
  }
};

// Funções para seleção de itens
const selectItem = item => {
  selectedItemId.value = item.id;
  showItemSelector.value = false;
};

const selectConversation = conversation => {
  selectedConversationId.value = conversation.id;
  showConversationSelector.value = false;
};

const selectContact = contact => {
  selectedContactId.value = contact.id;
  showContactSelector.value = false;
};

// Computed para filtrar listas
const filteredKanbanItems = computed(() => {
  if (!itemSearchTerm.value) return props.kanbanItems;
  return props.kanbanItems.filter(item =>
    (item.title || '')
      .toLowerCase()
      .includes(itemSearchTerm.value.toLowerCase())
  );
});

const filteredConversations = computed(() => {
  if (!conversationSearchTerm.value) return props.conversations;
  return props.conversations.filter(conv =>
    ((conv.title || '') + ' ' + (conv.description || ''))
      .toLowerCase()
      .includes(conversationSearchTerm.value.toLowerCase())
  );
});

const filteredContactsList = computed(() => {
  if (!contactSearchTerm.value) return props.contactsList;
  return props.contactsList.filter(contact =>
    (contact.name || '')
      .toLowerCase()
      .includes(contactSearchTerm.value.toLowerCase())
  );
});

// Refs para busca
const itemSearchTerm = ref('');
const conversationSearchTerm = ref('');
const contactSearchTerm = ref('');

// Computed para filtrar lista de agentes
const filteredAgentList = computed(() => {
  if (!agentSearch.value) return props.agentList;
  return props.agentList.filter(agent =>
    (agent.name || '').toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

// Função para obter detalhes de item vinculado
const getLinkedItemDetails = itemId => {
  return props.kanbanItems.find(item => item.id === itemId);
};

// Função para remover anexo
const removeAttachment = async attachment => {
  try {
    const url = `/api/v1/accounts/${KanbanAPI.accountId}/kanban/items/${props.item?.id}/attachments/${attachment.id}`;
    await axios.delete(url);
    // Recarregar checklists após remover anexo
    await fetchChecklists();
  } catch (error) {
    console.error('Erro ao remover anexo:', error.message);
  }
};

// Função para abrir preview de imagem
const openImagePreview = url => {
  window.open(url, '_blank');
};

// Função para buscar checklists do item
const fetchChecklists = async () => {
  try {
    loadingChecklists.value = true;
    const response = await KanbanAPI.getChecklists(props.item.id);
    internalChecklists.value = Array.isArray(response.data.checklist) ? response.data.checklist : [];
  } catch (error) {
    console.error('Erro ao buscar checklists:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao carregar checklists',
      action: { type: 'error' },
    });
  } finally {
    loadingChecklists.value = false;
  }
};

// Funções para gerenciar agentes dos itens do checklist
const assignAgentToChecklistItem = async (checklistItem, agentId) => {
  try {
    await KanbanAPI.assignAgentToChecklistItem(props.item?.id, checklistItem.id, agentId);
    // Recarregar checklists após atribuição
    await fetchChecklists();
  } catch (error) {
    console.error('Erro ao atribuir agente ao item do checklist:', error);
  }
};

const removeAgentFromChecklistItem = async checklistItem => {
  try {
    await KanbanAPI.removeAgentFromChecklistItem(props.item?.id, checklistItem.id);
    // Recarregar checklists após remoção
    await fetchChecklists();
  } catch (error) {
    console.error('Erro ao remover agente do item do checklist:', error);
  }
};

// Função para abrir modal de atribuição de agente para item do checklist
const openAgentAssignModalForChecklistItem = checklistItem => {
  selectedChecklistItemForAgent.value = checklistItem;
  showAgentAssignModal.value = true;
};

// Função para fechar modal de atribuição de agente
const closeAgentAssignModal = () => {
  showAgentAssignModal.value = false;
  selectedChecklistItemForAgent.value = null;
  selectedAgentId.value = null;
  agentSearch.value = '';
  agentAssignLoading.value = false;
};

// Função para abrir modal de detalhes do item
const openItemDetailsModal = checklistItem => {
  selectedChecklistItemDetails.value = checklistItem;
  showItemDetailsModal.value = true;
};

// Função para fechar modal de detalhes do item
const closeItemDetailsModal = () => {
  showItemDetailsModal.value = false;
  selectedChecklistItemDetails.value = null;
};

// Função para atribuir agente via modal
const assignAgentFromModal = async () => {
  if (!selectedAgentId.value || agentAssignLoading.value) return;
  if (!selectedChecklistItemForAgent.value) return;

  agentAssignLoading.value = true;
  try {
    await assignAgentToChecklistItem(
      selectedChecklistItemForAgent.value,
      selectedAgentId.value
    );
    closeAgentAssignModal();
    await fetchChecklists(); // Recarregar checklists após atribuição
    emitter.emit('newToastMessage', {
      message: 'Agente atribuído com sucesso ao item do checklist',
      type: 'success',
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao atribuir agente ao item do checklist',
      type: 'error',
    });
  } finally {
    agentAssignLoading.value = false;
  }
};

// Carregar checklists quando o componente for montado
onMounted(() => {
  if (props.item?.id) {
    fetchChecklists();
  }
});

// Função para alternar ordenação do checklist
const toggleChecklistSortOrder = () => {
  const sortOptions = ['newest', 'oldest'];
  const currentIndex = sortOptions.indexOf(checklistSortOrder.value);
  const nextIndex = (currentIndex + 1) % sortOptions.length;
  checklistSortOrder.value = sortOptions[nextIndex];
};

// Função para obter o texto da ordenação atual do checklist
const getChecklistSortOrderText = () => {
  switch (checklistSortOrder.value) {
    case 'newest':
      return 'Mais recentes';
    case 'oldest':
      return 'Mais antigas';
    default:
      return 'Mais recentes';
  }
};
</script>

<template>
  <div class="space-y-4">
    <!-- Header com estatísticas -->
    <div class="checklist-header">
      <div class="flex items-center justify-between">
        <div class="checklist-stats">
          <div class="flex items-center gap-4">
            <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-loader-icon lucide-loader text-slate-600 dark:text-slate-400">
                <path d="M12 2v4"/>
                <path d="m16.2 7.8 2.9-2.9"/>
                <path d="M18 12h4"/>
                <path d="m16.2 16.2 2.9 2.9"/>
                <path d="M12 18v4"/>
                <path d="m4.9 19.1 2.9-2.9"/>
                <path d="M2 12h4"/>
                <path d="m4.9 4.9 2.9 2.9"/>
              </svg>
              {{ t('KANBAN.FORM.CHECKLIST.TITLE') }}
            </h3>
            <div class="flex items-center gap-2 flex-1">
              <div class="progress-bar flex-1">
                <div
                  class="progress-fill"
                  :style="{ width: `${checklistStats.percentage}%` }"
                />
              </div>
              <span class="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                {{ checklistStats.completed }}/{{ checklistStats.total }}
                {{ checklistStats.percentage }}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Campo de texto para novo item -->
    <div class="checklist-input-section">
      <Editor
        v-model="currentChecklistItem"
        :placeholder="t('KANBAN.FORM.CHECKLIST.PLACEHOLDER')"
        :max-length="1000"
        :show-character-count="true"
        :enable-variables="false"
        :enable-canned-responses="false"
        :enable-captain-tools="false"
        :enabled-menu-options="[]"
      />

      <!-- Campos de prazo e prioridade lado a lado -->
      <div class="flex items-start gap-4 mt-2">
        <!-- Campo de prazo -->
        <div class="flex-1">
          <label class="block text-sm text-slate-600 dark:text-slate-400 mb-1">
            <fluent-icon icon="calendar" size="14" class="inline mr-1" />
            Prazo:
          </label>
          <input
            v-model="checklistDueDate"
            type="date"
            class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
            :min="new Date().toISOString().split('T')[0]"
          />
        </div>

        <!-- Campo de prioridade -->
        <div class="flex-1">
          <label class="block text-sm text-slate-600 dark:text-slate-400 mb-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline mr-1 text-slate-600 dark:text-slate-400">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            Prioridade:
          </label>
          <select
            v-model="checklistPriority"
            class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
          >
            <option value="none">Nenhuma</option>
            <option value="low">Baixa</option>
            <option value="medium">Média</option>
            <option value="high">Alta</option>
            <option value="urgent">Urgente</option>
          </select>
        </div>
      </div>

      <!-- Botões de ação -->
      <div class="flex items-center justify-between mt-2">
        <!-- Botão único de vinculação com dropdown -->
        <div class="relative" v-if="isStacklab">
          <!-- Botão principal de vinculação -->
          <button
            class="action-button hover:bg-slate-100 dark:hover:bg-slate-600"
            @click="toggleLinkDropdown"
            :class="{ 'bg-slate-100 dark:bg-slate-600': showLinkDropdown }"
          >
            <fluent-icon
              :icon="showLinkDropdown ? 'dismiss' : 'attach'"
              size="20"
            />
          </button>

            <!-- Dropdown de opções -->
            <div
              v-if="showLinkDropdown"
              class="link-dropdown absolute top-full mt-2 left-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-2 min-w-[180px] flex flex-col items-start"
            >
              <!-- Opção de upload de arquivo -->
              <FileUpload
                ref="upload"
                :accept="ALLOWED_FILE_TYPES"
                @input-file="handleChecklistAttachment"
              >
                <button
                  class="link-dropdown-item"
                  :disabled="isUploadingAttachment"
                >
                  <fluent-icon icon="attach" size="16" class="text-slate-600 dark:text-slate-400" />
                  <span>{{ isUploadingAttachment ? '...' : 'Anexar arquivo' }}</span>
                </button>
              </FileUpload>

              <!-- Separador -->
              <div class="border-t border-slate-200 dark:border-slate-700 my-1"></div>

              <!-- Opções de vinculação -->
              <button
                class="link-dropdown-item"
                @click="selectLinkType('item')"
              >
                <fluent-icon icon="link" size="16" class="text-blue-600 dark:text-blue-400" />
                <span>{{ t('KANBAN.FORM.NOTES.LINK_ITEM') }}</span>
              </button>
              <button
                class="link-dropdown-item"
                @click="selectLinkType('conversation')"
              >
                <fluent-icon icon="chat" size="16" class="text-slate-600 dark:text-slate-400" />
                <span>{{ t('KANBAN.FORM.NOTES.LINK_CONVERSATION') }}</span>
              </button>
              <button
                class="link-dropdown-item"
                @click="selectLinkType('contact')"
              >
                <fluent-icon icon="person" size="16" class="text-purple-600 dark:text-purple-400" />
                <span>{{ t('KANBAN.FORM.NOTES.LINK_CONTACT') }}</span>
              </button>
            </div>
          </div>

        <button
          class="primary-button"
          :disabled="!currentChecklistItem.trim()"
          @click="handleAddChecklistItem"
        >
          <fluent-icon icon="add" size="16" />
        </button>
      </div>

      <!-- Preview do arquivo selecionado -->
      <div
        v-if="selectedFileName"
        class="flex items-center gap-2 px-2 py-1 text-xs text-slate-600 dark:text-slate-400"
      >
        <fluent-icon icon="document" size="12" />
        <span class="truncate">{{ selectedFileName }}</span>
      </div>
    </div>

    <!-- Preview das seleções ativas -->
    <div
      v-if="
        selectedItemId || selectedConversationId || selectedContactId
      "
      class="selected-links-preview mt-2 mb-3 space-y-2"
    >
      <!-- Item selecionado -->
      <div v-if="selectedItemId" class="selected-link">
        <div
          class="flex items-center justify-between p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
        >
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="link"
              size="14"
              class="text-blue-600 dark:text-blue-400"
            />
            <span class="text-sm text-blue-700 dark:text-blue-300">
              {{
                getLinkedItemDetails(selectedItemId)?.title ||
                `Item #${selectedItemId}`
              }}
            </span>
          </div>
          <button
            class="p-1 text-blue-400 hover:text-blue-600 dark:hover:text-blue-300"
            @click="selectedItemId = null"
          >
            <fluent-icon icon="dismiss" size="12" />
          </button>
        </div>
      </div>

      <!-- Conversa selecionada -->
      <div v-if="selectedConversationId" class="selected-link">
        <div
          class="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
        >
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="chat"
              size="14"
              class="text-green-600 dark:text-green-400"
            />
            <span class="text-sm text-green-700 dark:text-green-300">
              Conversa #{{ selectedConversationId }}
            </span>
          </div>
          <button
            class="p-1 text-green-400 hover:text-green-600 dark:hover:text-green-300"
            @click="selectedConversationId = null"
          >
            <fluent-icon icon="dismiss" size="12" />
          </button>
        </div>
      </div>

      <!-- Contato selecionado -->
      <div v-if="selectedContactId" class="selected-link">
        <div
          class="flex items-center justify-between p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800"
        >
          <div class="flex items-center gap-2">
            <fluent-icon
              icon="person"
              size="14"
              class="text-purple-600 dark:text-purple-400"
            />
            <span
              class="text-sm text-purple-700 dark:text-purple-300"
            >
              {{
                contactsList.find(c => c.id === selectedContactId)
                  ?.name || `Contato #${selectedContactId}`
              }}
            </span>
          </div>
          <button
            class="p-1 text-purple-400 hover:text-purple-600 dark:hover:text-purple-300"
            @click="selectedContactId = null"
          >
            <fluent-icon icon="dismiss" size="12" />
          </button>
        </div>
      </div>
    </div>

    <!-- Seletor de itens -->
    <Modal
      v-if="showItemSelector"
      :show="showItemSelector"
      @close="showItemSelector = false"
    >
      <div
        class="item-selector p-0 bg-transparent border-none shadow-none"
      >
        <div
          class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
        >
          <h4
            class="text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {{ t('KANBAN.FORM.NOTES.SELECT_ITEM') }}
          </h4>
        </div>
        <!-- Caixa de busca -->
        <div class="px-4 py-2">
          <input
            v-model="itemSearchTerm"
            type="text"
            class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
            placeholder="Buscar..."
          />
        </div>
        <div class="max-h-[280px] overflow-y-auto custom-scrollbar">
          <div
            v-if="loadingItems"
            class="p-4 text-center text-sm text-slate-500"
          >
            <span class="loading-spinner w-4 h-4 mr-2" />
            {{ t('KANBAN.LOADING') }}
          </div>
          <div
            v-else-if="filteredKanbanItems.length === 0"
            class="p-4 text-center"
          >
            <p class="text-sm text-slate-500">
              {{ t('KANBAN.FORM.NOTES.NO_ITEMS') }}
            </p>
          </div>
          <div
            v-else
            class="divide-y divide-slate-100 dark:divide-slate-700"
          >
            <button
              v-for="item in filteredKanbanItems"
              :key="item.id"
              class="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:bg-slate-50 dark:focus:bg-slate-700"
              @click="selectItem(item)"
            >
              <div class="flex items-center gap-3">
                <!-- Prioridade -->
                <div
                  class="flex-shrink-0 w-1.5 h-5 rounded-full"
                  :class="{
                    'bg-n-ruby-9': item.priority === 'high',
                    'bg-yellow-500': item.priority === 'medium',
                    'bg-green-500': item.priority === 'low',
                    'bg-slate-300': item.priority === 'none',
                  }"
                />
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <h4
                    class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate flex-1"
                  >
                    {{ item.title }}
                  </h4>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 whitespace-nowrap"
                  >
                    {{ item.stage_name }}
                  </span>
                  <span
                    class="flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap"
                  >
                    <fluent-icon icon="calendar" size="12" />
                    {{ item.createdAt }}
                  </span>
                </div>
              </div>
              <p
                v-if="item.description"
                class="mt-1 ml-6 text-xs text-slate-500 dark:text-slate-400 line-clamp-1"
              >
                {{ item.description }}
              </p>
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Seletor de conversas -->
    <Modal
      v-if="showConversationSelector"
      :show="showConversationSelector"
      @close="showConversationSelector = false"
    >
      <div
        class="conversation-selector p-0 bg-transparent border-none shadow-none"
      >
        <div
          class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
        >
          <h4
            class="text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {{ t('KANBAN.FORM.NOTES.SELECT_CONVERSATION') }}
          </h4>
        </div>
        <!-- Caixa de busca -->
        <div class="px-4 py-2">
          <input
            v-model="conversationSearchTerm"
            type="text"
            class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
            placeholder="Buscar..."
          />
        </div>
        <div class="max-h-[280px] overflow-y-auto custom-scrollbar">
          <div
            v-if="loadingConversations"
            class="p-4 text-center text-sm text-slate-500"
          >
            <span class="loading-spinner w-4 h-4 mr-2" />
            {{ t('KANBAN.LOADING') }}
          </div>
          <div
            v-else-if="filteredConversations.length === 0"
            class="p-4 text-center"
          >
            <p class="text-sm text-slate-500">
              {{ t('KANBAN.FORM.NOTES.NO_CONVERSATIONS') }}
            </p>
          </div>
          <div
            v-else
            class="divide-y divide-slate-100 dark:divide-slate-700"
          >
            <button
              v-for="conversation in filteredConversations"
              :key="conversation.id"
              class="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:bg-slate-50 dark:focus:bg-slate-700"
              @click="selectConversation(conversation)"
            >
              <div class="flex items-center gap-3">
                <div
                  class="flex-shrink-0 w-1.5 h-5 rounded-full"
                  :class="{
                    'bg-green-500': conversation.status === 'open',
                    'bg-yellow-500': conversation.status === 'pending',
                    'bg-slate-500': conversation.status === 'resolved',
                  }"
                />
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <h4
                    class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate flex-1"
                  >
                    #{{ conversation.id }} - {{ conversation.title }}
                  </h4>
                  <span
                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 whitespace-nowrap"
                  >
                    {{ conversation.channel_type }}
                  </span>
                  <span
                    class="flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap"
                  >
                    <fluent-icon icon="contact-card" size="12" />
                    {{ conversation.created_at }}
                  </span>
                </div>
              </div>
              <p
                v-if="conversation.description"
                class="mt-1 ml-6 text-xs text-slate-500 dark:text-slate-400 line-clamp-1"
              >
                {{ conversation.description }}
              </p>
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Seletor de contatos -->
    <Modal
      v-if="showContactSelector"
      :show="showContactSelector"
      @close="showContactSelector = false"
    >
      <div
        class="contact-selector p-0 bg-transparent border-none shadow-none"
      >
        <div
          class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
        >
          <h4
            class="text-sm font-medium text-slate-700 dark:text-slate-300"
          >
            {{ t('KANBAN.FORM.NOTES.SELECT_CONTACT') }}
          </h4>
        </div>
        <!-- Caixa de busca -->
        <div class="px-4 py-2">
          <input
            v-model="contactSearchTerm"
            type="text"
            class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
            placeholder="Buscar..."
          />
        </div>
        <div class="max-h-[280px] overflow-y-auto custom-scrollbar">
          <div
            v-if="loadingContacts"
            class="p-4 text-center text-sm text-slate-500"
          >
            <span class="loading-spinner w-4 h-4 mr-2" />
            {{ t('KANBAN.LOADING') }}
          </div>
          <div
            v-else-if="filteredContactsList.length === 0"
            class="p-4 text-center"
          >
            <p class="text-sm text-slate-500">
              {{ t('KANBAN.FORM.NOTES.NO_CONTACTS') }}
            </p>
          </div>
          <div
            v-else
            class="divide-y divide-slate-100 dark:divide-slate-700"
          >
            <button
              v-for="contact in filteredContactsList"
              :key="contact.id"
              class="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:bg-slate-50 dark:focus:bg-slate-700"
              @click="selectContact(contact)"
            >
              <div class="flex items-center gap-3">
                <Avatar
                  v-if="contact.avatar_url"
                  :src="contact.avatar_url"
                  :name="contact.name"
                  :size="24"
                />
                <div class="flex items-center gap-2">
                  <h3
                    class="text-sm font-medium text-slate-900 dark:text-slate-100"
                  >
                    {{ contact.name }}
                  </h3>
                </div>
              </div>
              <div
                v-if="contact.phone || contact.last_activity_at"
                class="mt-1 ml-9 flex items-center gap-4 text-xs text-slate-500"
              >
                <span
                  v-if="contact.phone"
                  class="flex items-center gap-1"
                >
                  <fluent-icon icon="call" size="12" />
                  {{ contact.phone }}
                </span>
                <span
                  v-if="contact.last_activity_at"
                  class="flex items-center gap-1"
                >
                  <fluent-icon icon="alarm" size="12" />
                  {{ t('KANBAN.FORM.NOTES.LAST_ACTIVITY') }}:
                  {{ contact.last_activity_at }}
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Modal de atribuição de agente para item do checklist -->
    <Modal
      v-model:show="showAgentAssignModal"
      :on-close="closeAgentAssignModal"
      :show-close-button="true"
      size="medium"
    >
      <div
        class="p-4 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800 shadow-lg"
      >
        <h3 class="text-lg font-medium mb-4">Gerenciar Agentes Atribuídos</h3>
        <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
          {{ selectedChecklistItemForAgent?.text }}
        </p>

        <!-- Seção de agentes já atribuídos -->
        <div v-if="selectedChecklistItemForAgent && getChecklistItemAgent(selectedChecklistItemForAgent)" class="mb-4">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Agente Atribuído
          </h4>
          <div class="space-y-2">
            <div class="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-md">
              <div class="flex items-center gap-2">
                <Avatar
                  :name="getChecklistItemAgent(selectedChecklistItemForAgent)?.name"
                  :src="getChecklistItemAgent(selectedChecklistItemForAgent)?.avatar_url || ''"
                  :size="24"
                />
                <div>
                  <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {{ getChecklistItemAgent(selectedChecklistItemForAgent)?.name }}
                  </div>
                </div>
              </div>
              <button
                class="p-1 text-slate-400 hover:text-red-500 transition-colors"
                @click="removeAgentFromChecklistItem(selectedChecklistItemForAgent)"
              >
                <fluent-icon icon="delete" size="16" />
              </button>
            </div>
          </div>
        </div>

        <!-- Seção para adicionar novo agente -->
        <div class="border-t border-slate-200 dark:border-slate-700 pt-4">
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {{ selectedChecklistItemForAgent && getChecklistItemAgent(selectedChecklistItemForAgent) ? 'Alterar Agente' : 'Adicionar Novo Agente' }}
          </h4>
          <input
            v-model="agentSearch"
            type="text"
            class="w-full mb-3 px-3 py-2 border border-slate-100 dark:border-slate-800 rounded-md text-xs bg-white dark:bg-slate-800 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            placeholder="Buscar agentes..."
          />
          <div class="space-y-2 mb-4 max-h-40 overflow-y-auto">
            <button
              v-for="agent in filteredAgentList"
              :key="agent.id"
              type="button"
              class="flex items-center w-full px-3 py-1.5 rounded-md transition-colors gap-2 border border-slate-100 dark:border-slate-800 text-xs font-normal hover:bg-slate-100 dark:hover:bg-slate-800"
              :class="[
                selectedAgentId === agent.id
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 font-bold text-woot-600'
                  : '',
                getChecklistItemAgent(selectedChecklistItemForAgent)?.id === agent.id
                  ? 'opacity-50 cursor-not-allowed'
                  : '',
              ]"
              :disabled="getChecklistItemAgent(selectedChecklistItemForAgent)?.id === agent.id"
              @click="selectedAgentId = agent.id"
            >
              <Avatar
                :name="agent.name || 'Agente'"
                :src="agent.avatar_url || ''"
                :size="24"
                class="mr-2"
              />
              <span class="truncate">{{ agent.name || 'Agente' }}</span>
              <span
                v-if="getChecklistItemAgent(selectedChecklistItemForAgent)?.id === agent.id"
                class="ml-auto text-slate-400 text-xs"
                >Já atribuído</span
              >
              <span
                v-else-if="selectedAgentId === agent.id"
                class="ml-auto text-woot-500 text-xs font-semibold"
                >Selecionado</span
              >
            </button>
            <div
              v-if="filteredAgentList.length === 0"
              class="px-3 py-2 text-slate-400 text-xs"
            >
              Nenhum agente encontrado
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="closeAgentAssignModal"
            >Fechar</Button
          >
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :isLoading="agentAssignLoading"
            :disabled="
              !selectedAgentId ||
              agentAssignLoading ||
              getChecklistItemAgent(selectedChecklistItemForAgent)?.id === selectedAgentId
            "
            @click="assignAgentFromModal"
            >{{ selectedChecklistItemForAgent && getChecklistItemAgent(selectedChecklistItemForAgent) ? 'Alterar Agente' : 'Adicionar Agente' }}</Button
          >
        </div>
      </div>
    </Modal>

    <!-- Modal de detalhes do item do checklist -->
    <Modal
      v-model:show="showItemDetailsModal"
      :on-close="closeItemDetailsModal"
      :show-close-button="true"
      size="large"
    >
      <div
        v-if="selectedChecklistItemDetails"
        class="p-6 bg-white dark:bg-slate-900 rounded-md border border-slate-100 dark:border-slate-800 max-h-[80vh] overflow-y-auto"
      >
        <!-- Header do modal -->
        <div class="flex items-start justify-between mb-6">
          <div class="flex items-center gap-3">
            <!-- Título do item -->
            <div class="flex-1">
              <div class="flex items-center gap-3">
                <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  Detalhes do Item #{{ selectedChecklistItemDetails.id }}
                </h2>

                <!-- Status do item -->
                <div class="flex items-center gap-2">
                  <button
                    class="w-6 h-6 rounded border-2 flex items-center justify-center transition-all"
                    :class="{
                      'bg-green-500 border-green-500 text-white': selectedChecklistItemDetails.completed,
                      'border-slate-300 dark:border-slate-600': !selectedChecklistItemDetails.completed
                    }"
                  >
                    <fluent-icon
                      v-if="selectedChecklistItemDetails.completed"
                      icon="checkmark"
                      size="14"
                      class="text-white"
                    />
                  </button>
                  <span class="text-sm font-medium px-2 py-1 rounded-full"
                        :class="{
                          'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300': selectedChecklistItemDetails.completed,
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300': !selectedChecklistItemDetails.completed
                        }">
                    {{ selectedChecklistItemDetails.completed ? 'Concluído' : 'Pendente' }}
                  </span>
                </div>
              </div>
              <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Item do checklist
              </p>
            </div>
          </div>
        </div>

        <!-- Conteúdo principal -->
        <div class="space-y-6">
          <!-- Datas de criação e atualização -->
          <div class="flex items-center gap-6 text-sm text-slate-600 dark:text-slate-400">
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-500 dark:text-slate-500">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
                <line x1="12" y1="14" x2="12" y2="18"/>
                <line x1="9" y1="16" x2="15" y2="16"/>
              </svg>
              <span class="font-medium">Criado em:</span>
              <span class="text-slate-700 dark:text-slate-300">{{ formatDate(selectedChecklistItemDetails.created_at) }}</span>
            </div>

            <div v-if="selectedChecklistItemDetails.updated_at" class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-500 dark:text-slate-500">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span class="font-medium">Atualizado em:</span>
              <span class="text-slate-700 dark:text-slate-300">{{ formatDate(selectedChecklistItemDetails.updated_at) }}</span>
            </div>

            <div v-if="selectedChecklistItemDetails.due_date" class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-500 dark:text-slate-500">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              <span class="font-medium">Prazo:</span>
              <span class="text-slate-700 dark:text-slate-300">{{ formatDate(selectedChecklistItemDetails.due_date) }}</span>
            </div>
          </div>

          <!-- Texto do item -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Descrição
            </label>
            <div
              class="prose prose-sm max-w-none p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
              v-html="renderMarkdown(selectedChecklistItemDetails.text)"
            ></div>
          </div>

          <!-- Prioridade -->
          <div v-if="getChecklistItemPriority(selectedChecklistItemDetails).label !== 'Nenhuma'">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Prioridade
            </label>
            <div class="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <span
                class="inline-flex items-center gap-2 px-3 py-1 text-sm font-medium rounded-full"
                :class="{
                  'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300': getChecklistItemPriority(selectedChecklistItemDetails).color === 'red',
                  'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300': getChecklistItemPriority(selectedChecklistItemDetails).color === 'orange',
                  'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300': getChecklistItemPriority(selectedChecklistItemDetails).color === 'yellow',
                  'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300': getChecklistItemPriority(selectedChecklistItemDetails).color === 'green',
                }"
              >
                <svg
                  v-if="getChecklistItemPriority(selectedChecklistItemDetails).iconSvg"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  v-html="getChecklistItemPriority(selectedChecklistItemDetails).iconSvg"
                  class="flex-shrink-0"
                ></svg>
                {{ getChecklistItemPriority(selectedChecklistItemDetails).label }}
              </span>
            </div>
          </div>

          <!-- Agente responsável -->
          <div v-if="getChecklistItemAgent(selectedChecklistItemDetails)">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Responsável
            </label>
            <div class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
              <Avatar
                :name="getChecklistItemAgent(selectedChecklistItemDetails).name"
                :src="getChecklistItemAgent(selectedChecklistItemDetails).avatar_url"
                :size="32"
              />
              <div>
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {{ getChecklistItemAgent(selectedChecklistItemDetails).name }}
                </div>
                <div class="text-xs text-slate-500 dark:text-slate-400">
                  Agente responsável
                </div>
              </div>
            </div>
          </div>

          <!-- Vinculações -->
          <div v-if="selectedChecklistItemDetails.linked_item_id || selectedChecklistItemDetails.linked_conversation_id || selectedChecklistItemDetails.linked_contact_id">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Vinculações
            </label>
            <div class="space-y-2">
              <!-- Item vinculado -->
              <div v-if="selectedChecklistItemDetails.linked_item_id" class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-blue-600 dark:text-blue-400">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                  <span class="text-sm text-blue-700 dark:text-blue-300">
                    Item: {{ getLinkedItemDetails(selectedChecklistItemDetails.linked_item_id)?.title || `#${selectedChecklistItemDetails.linked_item_id}` }}
                  </span>
                </div>
              </div>

              <!-- Conversa vinculada -->
              <div v-if="selectedChecklistItemDetails.linked_conversation_id" class="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-green-600 dark:text-green-400">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  <span class="text-sm text-green-700 dark:text-green-300">
                    Conversa: #{{ selectedChecklistItemDetails.linked_conversation_id }}
                  </span>
                </div>
              </div>

              <!-- Contato vinculado -->
              <div v-if="selectedChecklistItemDetails.linked_contact_id" class="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div class="flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-purple-600 dark:text-purple-400">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span class="text-sm text-purple-700 dark:text-purple-300">
                    Contato: {{ contactsList?.find?.(c => c.id === selectedChecklistItemDetails.linked_contact_id)?.name || `#${selectedChecklistItemDetails.linked_contact_id}` }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Anexos -->
          <div v-if="selectedChecklistItemDetails.attachments && selectedChecklistItemDetails.attachments.length > 0">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
              Anexos
            </label>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <!-- Imagens -->
              <div v-for="attachment in getImageAttachments(selectedChecklistItemDetails)" :key="attachment.id" class="relative">
                <img
                  :src="attachment.url"
                  :alt="attachment.filename"
                  class="w-full h-24 object-cover rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:opacity-90 transition-opacity"
                  @click="openImagePreview(attachment.url)"
                />
                <div class="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                  {{ attachment.filename }}
                </div>
              </div>

              <!-- Arquivos -->
              <div v-for="attachment in getNonImageAttachments(selectedChecklistItemDetails)" :key="attachment.id"
                   class="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                <div class="text-2xl">{{ getFileIcon(attachment) }}</div>
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                    {{ attachment.filename }}
                  </div>
                  <div class="text-xs text-slate-500 dark:text-slate-400">
                    Arquivo anexado
                  </div>
                </div>
                <a
                  :href="attachment.url"
                  target="_blank"
                  class="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10,17 15,12 10,7"/>
                    <line x1="15" x2="3" y1="12" y2="12"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

        </div>

      </div>
    </Modal>

    <!-- Botão de ordenação acima da listagem -->
    <div class="flex justify-end items-center mb-4">
      <button
        v-if="displayChecklists.length > 1"
        class="checklist-sort-button"
        @click="toggleChecklistSortOrder"
        :title="getChecklistSortOrderText()"
      >
        <fluent-icon icon="arrow-sort" size="16" />
        <span class="ml-1">{{ getChecklistSortOrderText() }}</span>
      </button>
    </div>

    <!-- Seção de checklist -->
    <div class="checklist-section">
      <!-- Lista de itens do checklist -->
      <div class="space-y-2">
        <div
          v-for="checklistItem in filteredChecklistItems"
          :key="checklistItem.id"
          class="checklist-item group cursor-pointer"
          :class="{ 'completed': checklistItem.completed }"
          @click="openItemDetailsModal(checklistItem)"
        >
          <div class="flex items-start gap-3">
            <!-- Checkbox -->
            <div class="flex-shrink-0 mt-1">
              <button
                class="checklist-checkbox"
                :class="{ 'checked': checklistItem.completed }"
                @click.stop="handleToggleChecklistItem(checklistItem)"
              >
                <fluent-icon
                  v-if="checklistItem.completed"
                  icon="checkmark"
                  size="12"
                  class="text-white"
                />
              </button>
            </div>

            <!-- Conteúdo do item -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <div class="checklist-text" v-html="renderMarkdown(checklistItem.text)">
                </div>

                <!-- Botões de ação -->
                <div class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                    @click.stop="handleEditChecklistItem(checklistItem)"
                  >
                    <fluent-icon icon="edit" size="14" />
                  </button>
                  <button
                    class="p-1 text-slate-400 hover:text-n-ruby-9 dark:hover:text-n-ruby-4"
                    @click.stop="handleDeleteChecklistItem(checklistItem.id)"
                  >
                    <fluent-icon icon="delete" size="14" />
                  </button>
                </div>
              </div>

              <!-- Vinculações do item -->
              <div
                v-if="
                  checklistItem.linked_item_id ||
                  checklistItem.linked_conversation_id ||
                  checklistItem.linked_contact_id
                "
                class="checklist-links mt-2"
              >
                <!-- Item vinculado -->
                <div v-if="checklistItem.linked_item_id" class="linked-item">
                  <div
                    class="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800"
                  >
                    <fluent-icon
                      icon="link"
                      size="12"
                      class="text-blue-600 dark:text-blue-400"
                    />
                    <span
                      class="text-xs text-blue-700 dark:text-blue-300"
                    >
                      {{ t('KANBAN.FORM.NOTES.LINKED_ITEM') }}:
                      {{
                        getLinkedItemDetails(checklistItem.linked_item_id)
                          ?.title || `#${checklistItem.linked_item_id}`
                      }}
                    </span>
                  </div>
                </div>

                <!-- Conversa vinculada -->
                <div
                  v-if="checklistItem.linked_conversation_id"
                  class="linked-conversation mt-1"
                >
                  <div
                    class="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                  >
                    <fluent-icon
                      icon="chat"
                      size="12"
                      class="text-green-600 dark:text-green-400"
                    />
                    <span
                      class="text-xs text-green-700 dark:text-green-300"
                    >
                      {{ t('KANBAN.FORM.NOTES.LINKED_CONVERSATION') }}:
                      #{{ checklistItem.linked_conversation_id }}
                    </span>
                  </div>
                </div>

                <!-- Contato vinculado -->
                <div
                  v-if="checklistItem.linked_contact_id"
                  class="linked-contact mt-1"
                >
                  <div
                    class="flex items-center gap-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800"
                  >
                    <fluent-icon
                      icon="person"
                      size="12"
                      class="text-purple-600 dark:text-purple-400"
                    />
                    <span
                      class="text-xs text-purple-700 dark:text-purple-300"
                    >
                      {{ t('KANBAN.FORM.NOTES.LINKED_CONTACT') }}:
                      {{
                        contactsList?.find?.(
                          c => c.id === checklistItem.linked_contact_id
                        )?.name || `#${checklistItem.linked_contact_id}`
                      }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Metadados do item -->
              <div class="checklist-metadata mt-2 w-full">
                <div class="flex items-center justify-between w-full">
                  <div class="flex items-center gap-2">
                    <!-- Prioridade -->
                    <div v-if="getChecklistItemPriority(checklistItem).label !== 'Nenhuma'" class="checklist-priority">
                      <span
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
                        :class="{
                          'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300': getChecklistItemPriority(checklistItem).color === 'red',
                          'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300': getChecklistItemPriority(checklistItem).color === 'orange',
                          'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300': getChecklistItemPriority(checklistItem).color === 'yellow',
                          'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300': getChecklistItemPriority(checklistItem).color === 'green',
                        }"
                      >
                        <svg
                          v-if="getChecklistItemPriority(checklistItem).iconSvg"
                          xmlns="http://www.w3.org/2000/svg"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          v-html="getChecklistItemPriority(checklistItem).iconSvg"
                          class="flex-shrink-0"
                        ></svg>
                        {{ getChecklistItemPriority(checklistItem).label }}
                      </span>
                    </div>

                    <Avatar
                      v-if="getChecklistItemAgent(checklistItem)"
                      :name="getChecklistItemAgent(checklistItem).name"
                      :src="getChecklistItemAgent(checklistItem).avatar_url"
                      :size="16"
                    />
                    <span class="checklist-author text-xs">
                      {{ getChecklistItemAgent(checklistItem)?.name || 'Sem autor' }}
                    </span>

                    <!-- Botão para atribuir agente -->
                    <button
                      v-if="isStacklab"
                      class="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 rounded transition-colors"
                      @click.stop="openAgentAssignModalForChecklistItem(checklistItem)"
                    >
                      <fluent-icon icon="person-add" size="12" />
                      <span v-if="getChecklistItemAgent(checklistItem)">Alterar agente</span>
                      <span v-else>Atribuir agente</span>
                    </button>
                  </div>

                  <span class="checklist-date text-xs text-slate-400">
                    {{ formatDate(checklistItem.created_at || new Date()) }}
                  </span>
                </div>
              </div>

              <!-- Prazo do item -->
              <div v-if="checklistItem.due_date" class="checklist-due-date-container mt-2">
                <span
                  class="checklist-due-date text-sm font-medium flex items-center gap-2 px-2 py-1 rounded-md border"
                  :class="{
                    'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800': new Date(checklistItem.due_date) < new Date() && !checklistItem.completed,
                    'text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800': new Date(checklistItem.due_date) >= new Date() && new Date(checklistItem.due_date) <= new Date(Date.now() + 24 * 60 * 60 * 1000) && !checklistItem.completed,
                    'text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700': new Date(checklistItem.due_date) > new Date(Date.now() + 24 * 60 * 60 * 1000) || checklistItem.completed
                  }"
                >
                  <fluent-icon icon="calendar" size="14" />
                  Prazo: {{ formatDate(checklistItem.due_date) }}
                </span>
              </div>

              <!-- Arquivos não-imagem -->
              <div v-if="hasNonImageAttachments(checklistItem)" class="checklist-files">
                <div
                  v-for="attachment in getNonImageAttachments(checklistItem)"
                  :key="attachment.id"
                  class="file-attachment"
                >
                  <div class="file-info">
                    <span class="file-icon">
                      {{ getFileIcon(attachment) }}
                    </span>
                    <a
                      :href="attachment.url"
                      target="_blank"
                      class="file-name"
                      :title="attachment.filename"
                    >
                      {{ attachment.filename }}
                    </a>
                  </div>
                  <button
                    class="file-action"
                    @click="removeAttachment(attachment)"
                  >
                    <fluent-icon icon="delete" size="12" />
                  </button>
                </div>
              </div>
            </div>

            <!-- Preview de imagens -->
            <div v-if="hasImageAttachments(checklistItem)" class="checklist-images">
              <div
                v-for="attachment in getImageAttachments(checklistItem)"
                :key="attachment.id"
                class="image-preview"
              >
                <img
                  :src="attachment.url"
                  :alt="attachment.filename"
                  class="preview-image"
                  @click="openImagePreview(attachment.url)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mensagem quando não há itens -->
      <div
        v-if="displayChecklists.length === 0"
        class="text-center py-8 text-slate-500"
      >
        <fluent-icon icon="list" size="48" class="mx-auto mb-4 opacity-50" />
        <p class="text-sm">{{ t('KANBAN.FORM.CHECKLIST.EMPTY') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Estilos específicos do componente ChecklistTab */
.checklist-header {
  @apply bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700;
}

.checklist-stats {
  @apply flex-1;
}

.progress-bar {
  @apply flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden;
}

.progress-fill {
  @apply h-full bg-green-500 transition-all duration-300 ease-out;
}

.checklist-input-section {
  @apply space-y-2;
}

.action-button {
  @apply flex items-center justify-center w-10 h-10 rounded-lg
         text-slate-600 hover:text-slate-900
         dark:text-slate-400 dark:hover:text-slate-200
         bg-slate-50 hover:bg-slate-100
         dark:bg-slate-700 dark:hover:bg-slate-600
         transition-all duration-200;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.primary-button {
  @apply flex items-center justify-center w-10 h-10 text-sm font-medium text-white
         bg-woot-500 hover:bg-woot-600
         dark:bg-woot-600 dark:hover:bg-woot-700
         rounded-lg transition-colors;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}

.checklist-section {
  @apply space-y-2;
}

.checklist-item {
  @apply bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700
         hover:border-slate-300 dark:hover:border-slate-600 transition-all duration-200;

  &.completed {
    @apply opacity-75;
  }

  &.completed .checklist-text {
    @apply line-through text-slate-500 dark:text-slate-400;
  }
}


.checklist-checkbox {
  @apply w-5 h-5 rounded bg-white dark:bg-slate-700 flex items-center justify-center
         hover:border-woot-500 transition-all duration-200;
  border: 1px solid #9ca3af;

  &.checked {
    @apply bg-green-500 border-green-500;
    border-color: #10b981;
  }
}

.checklist-text {
  @apply flex-1 text-slate-700 dark:text-slate-300 text-sm leading-relaxed
         break-words overflow-hidden;

  /* Estilos para markdown renderizado */
  :deep(p) {
    @apply mb-2 last:mb-0;
  }

  :deep(ul), :deep(ol) {
    @apply ml-4 mb-2;
  }

  :deep(li) {
    @apply mb-1;
  }

  :deep(strong), :deep(b) {
    @apply font-semibold;
  }

  :deep(em), :deep(i) {
    @apply italic;
  }

  :deep(code) {
    @apply bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded text-xs font-mono;
  }

  :deep(pre) {
    @apply bg-slate-100 dark:bg-slate-800 p-2 rounded text-xs font-mono overflow-x-auto mb-2;
  }

  :deep(a) {
    @apply text-woot-500 dark:text-woot-400 hover:underline;
  }

  :deep(blockquote) {
    @apply border-l-4 border-slate-300 dark:border-slate-600 pl-4 italic text-slate-600 dark:text-slate-400;
  }
}

.checklist-metadata {
  @apply flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400;
}

.checklist-author {
  @apply font-medium;
}

.checklist-priority {
  @apply flex-shrink-0;
}

.checklist-files {
  @apply mt-3 space-y-2 border-t border-slate-100 dark:border-slate-700 pt-3;
}

.file-attachment {
  @apply flex items-center justify-between p-2
         bg-slate-50 dark:bg-slate-700
         rounded-lg text-sm;
}

.file-info {
  @apply flex items-center gap-1.5 flex-1 min-w-0;
}

.file-icon {
  @apply flex-shrink-0 text-slate-400;
}

.file-name {
  @apply text-[10px] hover:text-woot-500 dark:hover:text-woot-400
         text-slate-700 dark:text-slate-300 max-w-[150px]
         whitespace-nowrap overflow-hidden text-ellipsis;
}

.file-action {
  @apply p-1.5 text-slate-400 hover:text-n-ruby-9
           dark:text-slate-500 dark:hover:text-n-ruby-4
         rounded-md hover:bg-slate-100 dark:hover:bg-slate-600
         transition-colors;
}

.checklist-images {
  @apply flex-shrink-0 flex flex-col gap-2;
}

.image-preview {
  @apply rounded-lg overflow-hidden bg-slate-50 dark:bg-slate-700;
}

.preview-image {
  @apply w-16 h-16 object-cover rounded-lg cursor-pointer
         hover:opacity-90 transition-opacity;
}

.checklist-links {
  @apply space-y-1 mt-2;
}

/* Dropdown de vinculação */
.link-dropdown {
  @apply z-50;
}

.link-dropdown-item {
  @apply flex items-center gap-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300
         hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors w-full text-left;
}

.link-dropdown-item:hover .fluent-icon {
  @apply opacity-80;
}

.link-dropdown-item:disabled {
  @apply opacity-60 cursor-not-allowed;
}

.link-dropdown-item:disabled:hover {
  @apply bg-transparent dark:bg-transparent;
}

/* Responsividade */
@media (max-width: 640px) {
  .checklist-item {
    @apply p-3;
  }

  .checklist-images {
    @apply flex-row flex-wrap;
  }

  .preview-image {
    @apply w-12 h-12;
  }

  .file-name {
    @apply max-w-[120px];
  }
}

/* Estilos para links selecionados */
.selected-links-preview {
  @apply border-t border-slate-200 dark:border-slate-700 pt-3;
}

.selected-link {
  @apply transition-all duration-200;
}

/* Estilos para vinculações */
.linked-item,
.linked-conversation,
.linked-contact {
  @apply transition-all duration-200;
}

/* Scrollbar customizado */
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: var(--color-scrollbar) transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar);
  border-radius: 3px;
}

.dark .custom-scrollbar {
  scrollbar-color: var(--color-scrollbar-dark) transparent;
}

.dark .custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: var(--color-scrollbar-dark);
}

/* Botão de ordenação do checklist */
.checklist-sort-button {
  @apply flex items-center px-3 py-1.5 text-sm font-medium
         text-slate-600 dark:text-slate-400
         bg-slate-100 hover:bg-slate-200
         dark:bg-slate-700 dark:hover:bg-slate-600
         rounded-lg transition-colors duration-200
         border border-slate-200 dark:border-slate-600;
}

.checklist-sort-button:hover {
  @apply text-slate-900 dark:text-slate-200;
}
</style>
